import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

const AuthContext = createContext();
const AUTH_TIMEOUT_MS = 8000;

const withTimeout = (promise, message) => Promise.race([
  promise,
  new Promise((_, reject) => {
    setTimeout(() => reject(new Error(message)), AUTH_TIMEOUT_MS);
  }),
]);

const authReducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        emailConfirmed: action.payload.emailConfirmed,
        user: action.payload.user,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        loading: false,
        isAuthenticated: false,
        user: null,
        emailConfirmed: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        emailConfirmed: false,
        loading: false,
        error: null,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload.user,
        emailConfirmed: action.payload.emailConfirmed,
        isAuthenticated: true,
        loading: false,
      };
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload
      };
    case 'SET_ERROR':
      return {
        ...state,
        loading: false,
        error: action.payload,
      };
    default:
      return state;
  }
};

const initialState = {
  isAuthenticated: false,
  emailConfirmed: false,
  user: null,
  loading: true,
  error: null,
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authReadyRef = useRef(false);

  const fetchAndSetUser = async (sbUser) => {
    try {
        const { data: profile, error } = await withTimeout(
          supabase
            .from('profiles')
            .select('*')
            .eq('id', sbUser.id)
            .single(),
          'Supabase nuk u përgjigj brenda kohës së lejuar.'
        );
        
      if (error && error.code !== 'PGRST116') {
        console.error("Error fetching user profile details:", error);
      }

      // Check if email is confirmed
      const emailConfirmed = !!sbUser.email_confirmed_at;
      
      const combined = {
        id: sbUser.id,
        email: sbUser.email,
        username: profile?.username || sbUser.user_metadata?.username || sbUser.email?.split('@')[0] || 'user',
        firstName: profile?.first_name || sbUser.user_metadata?.firstName || sbUser.user_metadata?.full_name || sbUser.user_metadata?.name || '',
        lastName: profile?.last_name || sbUser.user_metadata?.lastName || '',
        role: sbUser.app_metadata?.role || 'user',
        profession: profile?.profession || null,
        avatarUrl: profile?.avatar_url || profile?.avatarUrl || sbUser.user_metadata?.avatar_url || null,
        xp: profile?.xp ?? 50,
        level: profile?.level ?? 1,
        streak: profile?.streak ?? 1
      };
      
      dispatch({ type: 'SET_USER', payload: { user: combined, emailConfirmed } });
    } catch (e) {
      console.error('Failed to parse user details:', e);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  useEffect(() => {
    let mounted = true;
    const initializationTimeout = setTimeout(() => {
      if (mounted && !authReadyRef.current) {
        console.error('Auth initialization timed out.');
        authReadyRef.current = true;
        dispatch({
          type: 'SET_ERROR',
          payload: 'Lidhja me Supabase zgjati shumë. Kontrollo URL dhe çelësin në frontend/.env.',
        });
      }
    }, AUTH_TIMEOUT_MS + 1000);

    // Resolve the existing session once. The auth listener handles later sign-in/out events.
    withTimeout(
      supabase.auth.getSession(),
      'Lidhja me Supabase zgjati shumë. Kontrollo REACT_APP_SUPABASE_URL në frontend/.env.'
    )
      .then(({ data: { session } }) => {
        if (!mounted) return;
        authReadyRef.current = true;
        if (session?.user) {
          fetchAndSetUser(session.user);
        } else {
          dispatch({ type: 'LOGOUT' });
        }
      })
      .catch((error) => {
        if (!mounted) return;
        authReadyRef.current = true;
        console.error('Unable to connect to Supabase. Check REACT_APP_SUPABASE_URL and network access.', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Nuk mund të lidhemi me serverin e llogarisë. Kontrollo Supabase URL në frontend/.env dhe rinise frontend-in.',
        });
      });

    // 2. Listen to Auth state changes (catches email confirmation redirect)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!authReadyRef.current) return;
      if (session?.user) {
        fetchAndSetUser(session.user);
      } else {
        dispatch({ type: 'LOGOUT' });
      }
    });

    return () => {
      mounted = false;
      clearTimeout(initializationTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;

      // Block login if email is not confirmed
      if (!data.user.email_confirmed_at) {
        await supabase.auth.signOut();
        dispatch({ type: 'LOGIN_FAILURE', payload: 'Ju lutem konfirmoni email-in tuaj para se të hyni.' });
        return { success: false, needsConfirmation: true, error: 'Email not confirmed' };
      }
      
      await fetchAndSetUser(data.user);
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.message || 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: msg });
      return { success: false, error: msg };
    }
  };

  const loginWithProvider = async (provider) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
          ...(provider === 'azure' ? { scopes: 'email' } : {}),
        },
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error(`${provider} login error:`, error);
      const message = provider === 'azure'
        ? 'Hyrja me Microsoft dështoi. Kontrollo konfigurimin e ofruesit Microsoft në Supabase.'
        : 'Hyrja me Google dështoi. Kontrollo konfigurimin e ofruesit Google në Supabase.';
      dispatch({ type: 'SET_ERROR', payload: message });
      return { success: false, error: message };
    }
  };

  const register = async (username, email, password, profileDetails = {}) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username, ...profileDetails },
          // After confirmation Supabase redirects here:
          emailRedirectTo: `${window.location.origin}/login`,
        }
      });
      if (error) throw error;

      // Don't auto-login — user must confirm email first
      dispatch({ type: 'SET_LOADING', payload: false });
      return { success: true, needsConfirmation: true, email };
    } catch (error) {
      console.error('Registration error:', error);
      let msg = error.message || 'Registration failed';
      if (error.code === 'user_already_exists' || error.code === 'email_exists' || msg.toLowerCase().includes('already registered')) {
        msg = 'Ky email është tashmë i regjistruar. Përdor një email tjetër ose hyr në llogari.';
      }
      if (msg.toLowerCase().includes('rate limit') || error.status === 429) {
        msg = 'Limiti i dërgimit të email-eve u tejkalua (Supabase Rate Limit). Ju lutem prisni pak para se të provoni sërish, ose kontaktoni administratorin.';
      }
      dispatch({ type: 'LOGIN_FAILURE', payload: msg });
      return { success: false, error: msg };
    }
  };

  const resendConfirmationEmail = async (email) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
        }
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      let msg = error.message;
      if (msg && (msg.toLowerCase().includes('rate limit') || error.status === 429)) {
        msg = 'Limiti i dërgimit të email-eve u tejkalua. Ju lutem prisni 60 sekonda.';
      }
      return { success: false, error: msg };
    }
  };

  const logout = async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    await supabase.auth.signOut();
    dispatch({ type: 'LOGOUT' });
  };

  const updateUserStats = async (stats) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'No active session' };

      const { error } = await supabase
        .from('profiles')
        .update(stats)
        .eq('id', user.id);

      if (error) throw error;

      const newUser = { ...state.user, ...stats };
      dispatch({ type: 'SET_USER', payload: { user: newUser, emailConfirmed: state.emailConfirmed } });
      return { success: true };
    } catch (error) {
      console.error('Failed to update profile stats:', error);
      return { success: false, error: error.message || error };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { success: false, error: 'Nuk ka sesion aktiv.' };

      const { error } = await supabase
        .from('profiles')
        .update(profileData)
        .eq('id', user.id);

      if (error) throw error;

      const nextUser = {
        ...state.user,
        ...profileData,
        ...(profileData.avatar_url !== undefined ? { avatarUrl: profileData.avatar_url } : {}),
      };
      dispatch({ type: 'SET_USER', payload: { user: nextUser, emailConfirmed: state.emailConfirmed } });
      return { success: true };
    } catch (error) {
      console.error('Profile update error:', error);
      return { success: false, error: error.message || 'Dështoi përditësimi i profilit.' };
    }
  };

  const resetPassword = async (email) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Password reset error:', error);
      let msg = error.message || 'Dështoi dërgimi i email-it të rivendosjes.';
      if (msg.toLowerCase().includes('security') || msg.toLowerCase().includes('rate') || error.status === 429) {
        msg = 'Keni bërë shumë kërkesa. Për arsye sigurie, ju lutem prisni 60 sekonda para se të provoni sërish.';
      }
      return { success: false, error: msg };
    }
  };

  const updateEmail = async (email) => {
    try {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Email update error:', error);
      return { success: false, error: error.message || 'Dështoi ndryshimi i email-it.' };
    }
  };

  const updatePassword = async (newPassword) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Update password error:', error);
      return { success: false, error: error.message || 'Dështoi përditësimi i fjalëkalimit.' };
    }
  };

  const value = {
    ...state,
    login,
    loginWithProvider,
    register,
    logout,
    updateUserStats,
    updateProfile,
    resendConfirmationEmail,
    resetPassword,
    updateEmail,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};